"use client";

import { useEffect, type FormEvent } from "react";

import { TimingCitySelector } from "@/components/choghadiya/ChoghadiyaCitySelector";
import type { PlaceResult } from "@/lib/api/misc";
import {
  cityParamForPlace,
  normalizeCityParam,
  TIMING_CITY_PARAM,
  TIMING_DATE_PARAM,
  timingHubHref,
  type TimingLocale,
  type TimingTool,
} from "@/lib/timing-city-param";

/**
 * The city (and day) selector for a timing hub: a real `<form method="get">`
 * that navigates to `?city=<slug>&date=<YYYY-MM-DD>`.
 *
 * Without JavaScript it is a plain GET form over the 198 catalog cities, so a
 * crawler or a no-JS browser reaches the same computed page a click does.
 * With JavaScript the city select submits on change, an extra search box
 * reaches any Indian (or GeoNames) place, and the last choice is remembered in
 * localStorage: a visitor who returns to the bare hub is sent on to the city
 * they last chose. Nothing about the choice enters the server render except
 * through the URL.
 */

export const TIMING_CITY_STORAGE_KEY = "ish_timing_city";

interface CatalogCity {
  slug: string;
  name: string;
  state: string;
}

const COPY = {
  en: {
    city: "City",
    date: "Date",
    show: "Show",
    search: "Not listed?",
    searchButton: "Search any place",
  },
  hi: {
    city: "शहर",
    date: "तारीख",
    show: "देखें",
    search: "सूची में नहीं?",
    searchButton: "कोई भी स्थान खोजें",
  },
} as const;

function remember(city: string | null): void {
  if (!city) return;
  try {
    localStorage.setItem(TIMING_CITY_STORAGE_KEY, city);
  } catch {
    /* storage blocked: the URL still carries the choice */
  }
}

function recalled(): string | null {
  try {
    return normalizeCityParam(localStorage.getItem(TIMING_CITY_STORAGE_KEY) ?? undefined);
  } catch {
    return null;
  }
}

export default function TimingCityForm({
  tool,
  locale = "en",
  cities,
  city,
  cityName,
  date,
  today,
  min,
  max,
  dateSelectable = true,
  isBareHub = false,
}: {
  tool: TimingTool;
  locale?: TimingLocale;
  cities: readonly CatalogCity[];
  /** The rendered city's slug (the hub default when no `?city=` resolved). */
  city: string;
  /** Display name, used when `city` is not one of the catalog options. */
  cityName: string;
  date: string;
  today: string;
  min: string;
  max: string;
  /** Shubh Muhurat is a "now" tool and takes no date. */
  dateSelectable?: boolean;
  /** The plain hub URL (no query): restores the remembered city. */
  isBareHub?: boolean;
}) {
  const copy = COPY[locale];
  const action = timingHubHref(tool, locale);
  const inCatalog = cities.some((option) => option.slug === city);

  useEffect(() => {
    if (!isBareHub || window.location.search) return;
    const stored = recalled();
    if (stored && stored !== city) {
      window.location.replace(timingHubHref(tool, locale, { city: stored }));
    }
  }, [city, isBareHub, locale, tool]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    const form = event.currentTarget;
    const dateInput = form.elements.namedItem(TIMING_DATE_PARAM);
    // Today's page is the undated URL: omit `date` rather than mint a second
    // address for the same document (and the same edge-cache entry).
    if (dateInput instanceof HTMLInputElement && (!dateInput.value || dateInput.value === today)) {
      dateInput.disabled = true;
    }
    const cityInput = form.elements.namedItem(TIMING_CITY_PARAM);
    remember(cityInput instanceof HTMLSelectElement ? cityInput.value : null);
  }

  function handlePlace(place: PlaceResult) {
    const param = cityParamForPlace(place, cities);
    if (!param) return;
    remember(param);
    window.location.assign(
      timingHubHref(tool, locale, {
        city: param,
        date: dateSelectable && date !== today ? date : null,
      })
    );
  }

  return (
    <div className="card-sacred mb-6 p-4" data-testid="timing-city-form">
      <form
        method="get"
        action={action}
        onSubmit={handleSubmit}
        className="flex flex-wrap items-end gap-3"
      >
        <label className="flex min-w-[12rem] flex-1 flex-col text-body-small font-semibold text-ish-dark">
          {copy.city}
          <select
            name={TIMING_CITY_PARAM}
            defaultValue={city}
            onChange={(event) => event.currentTarget.form?.requestSubmit()}
            className="input-sacred mt-1 rounded-lg px-3 py-2 text-body font-normal text-ish-dark"
          >
            {inCatalog ? null : <option value={city}>{cityName}</option>}
            {cities.map((option) => (
              <option key={option.slug} value={option.slug}>
                {option.name}, {option.state}
              </option>
            ))}
          </select>
        </label>
        {dateSelectable ? (
          <label className="flex flex-col text-body-small font-semibold text-ish-dark">
            {copy.date}
            <input
              type="date"
              name={TIMING_DATE_PARAM}
              defaultValue={date}
              min={min}
              max={max}
              className="input-sacred mt-1 rounded-lg px-3 py-2 text-body font-normal text-ish-dark"
            />
          </label>
        ) : null}
        <button type="submit" className="btn-sacred rounded-lg px-4 py-2 text-body font-semibold">
          {copy.show}
        </button>
      </form>
      <div className="mt-2 text-center text-caption text-ish-text-faint">
        {copy.search}{" "}
        <TimingCitySelector
          city={{ name: copy.searchButton, latitude: 0, longitude: 0 }}
          onCitySelect={() => undefined}
          onPlaceSelect={handlePlace}
          // Shubh Muhurat is computed on the India clock; its hub ignores a
          // foreign city, so its search offers only Indian places.
          countryFilter={tool === "shubh-muhurat" ? "IN" : undefined}
        />
      </div>
    </div>
  );
}
